import { useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { STORAGE_KEY, SCHEMA_VERSION } from './constants';
import { initialGardenState } from './gardenReducer';
import type { GardenState } from './gardenReducer';

const SYNC_DEBOUNCE_MS = 800;

function isValidState(value: unknown): value is GardenState {
  const parsed = value as Partial<GardenState> | null;
  return (
    !!parsed &&
    parsed.version === SCHEMA_VERSION &&
    Array.isArray(parsed.elements) &&
    Array.isArray(parsed.shape) &&
    parsed.shape.length >= 3
  );
}

function readLegacyLocalState(): GardenState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export async function loadPersistedState(uid: string): Promise<GardenState> {
  const ref = doc(db, 'gardens', uid);
  const snapshot = await getDoc(ref);

  if (snapshot.exists() && isValidState(snapshot.data())) {
    return snapshot.data() as GardenState;
  }

  // First login for this account: carry over any garden that already
  // exists in this browser's localStorage instead of starting blank.
  const legacy = readLegacyLocalState();
  if (legacy) {
    await setDoc(ref, legacy);
    localStorage.removeItem(STORAGE_KEY);
    return legacy;
  }

  return initialGardenState;
}

export function useFirestoreSync(uid: string, state: GardenState, enabled: boolean): void {
  const stateRef = useRef(state);
  stateRef.current = state;

  // Debounced write: cancels and restarts on every state change while
  // `enabled`, so rapid edits collapse into one Firestore write.
  useEffect(() => {
    if (!enabled) return;
    const ref = doc(db, 'gardens', uid);
    const timeoutId = setTimeout(() => {
      void setDoc(ref, stateRef.current);
    }, SYNC_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [uid, state, enabled]);

  // Flush immediately when the tab is hidden/closed so the last edit
  // inside the debounce window isn't lost.
  useEffect(() => {
    if (!enabled) return;
    const ref = doc(db, 'gardens', uid);

    function flush() {
      void setDoc(ref, stateRef.current);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') flush();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', flush);
    };
  }, [uid, enabled]);
}
